import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  IFilter,
  IPopulate,
  IQueryParam,
  IUbicacion,
  IUbicacionBase,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { AuxiliaresModule } from '../../../../../../auxiliares/auxiliares.module';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../../../auxiliares/tabla/tabla.component';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../../../auxiliares/tabla/filtro/filtro.component';
import { DialogService } from '../../../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { UbicacionService } from '../../../../../../auxiliares/servicios/http/ubicacion.service';
import { ClienteJerarquiaService } from '../../../../../../auxiliares/servicios/cliente-jerarquia.service';
import {
  esc,
  filtroActivo,
  estadoActivo,
  htmlContacto,
  htmlDireccion,
  tooltipDireccion,
  htmlEntidad,
} from '../../presentacion-entidades';
import { CrearEditarHospitalComponent } from '../crear-editar-hospital/crear-editar-hospital.component';
import { FormularioDialogService } from '../../../../../../auxiliares/servicios/formulario-dialog.service';

type IHospital = IUbicacionBase<'Hospital'>;

const RUTA = 'main/emergencias/hospitales';

@Component({
  selector: 'app-listado-hospitales',
  imports: [AuxiliaresModule],
  templateUrl: './listado-hospitales.component.html',
})
export class HospitalesComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  private dialogService = inject(DialogService);
  private service = inject(UbicacionService);
  private router = inject(Router);
  private matDialog = inject(MatDialog);
  private formularios = inject(FormularioDialogService);
  private clienteJerarquia = inject(ClienteJerarquiaService);

  public name = 'HospitalesComponent';
  public datos?: IHospital[];
  public totalCount = 0;
  public columnas?: IColumnas<IHospital>[];
  private datos$?: Subscription;

  public botonCrear: ICrearBoton = {
    mostrar: this.helper.tienePermiso('Emergencias Médicas - Crear hospitales'),
    tooltip: 'Crear hospital',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crear(),
  };

  public search: IRegExpSearch = {
    fields: [
      'identificacion',
      'direccion',
      'valores.telefono',
      'valores.email',
    ],
  };

  // Filtros
  private cliente: IFiltroTabla = {
    filter: { field: 'idCliente' },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // El cliente elegido y toda su descendencia.
  private ancestro: IFiltroTabla = {
    filter: { field: 'idsAncestros', incluirPropio: true },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  private gestion: IFiltroTabla = {
    elementos: ['Público', 'Privado', 'Público-privado'],
    filter: { field: 'valores.gestion' },
    label: 'Gestión',
    tipo: 'select',
  };
  public filtros: IFiltroTabla[] = [
    this.gestion,
    filtroActivo('valores.activo'),
  ];

  private filter: IFilter<IUbicacion> = {};
  private populate: IPopulate[] = [{ path: 'cliente' }];
  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    sort: 'identificacion',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    childrenLevel: 0,
  };

  public listar(): void {
    this.datos$?.unsubscribe();
    this.datos$ = this.lista2.hospitals(this.query, 15).subscribe((data) => {
      const datos = data.datos as IHospital[];
      if (HelperService.cambiaronLosDatos(this.datos, datos)) {
        this.datos = datos;
      }
      this.totalCount = data.totalCount;
    });
  }

  // Acciones

  public crear(): void {
    this.abrirFormulario();
  }

  public editar(hospital: IHospital): void {
    this.abrirFormulario({ edit: hospital });
  }

  /** Crear/editar en diálogo; al guardar se refresca el listado. */
  private abrirFormulario(data?: { edit: IHospital }): void {
    const ref = this.formularios.abrir(CrearEditarHospitalComponent, {
      tamano: 'mediano',
      data,
    });
    ref.afterClosed().subscribe((guardado) => {
      if (guardado) this.listar();
    });
  }

  public detalles(hospital: IHospital): void {
    this.router.navigate([RUTA, 'detalles', hospital._id]);
  }

  public async eliminar(hospital: IHospital): Promise<void> {
    const ok = await this.dialogService.confirm(
      'Eliminar hospital',
      `¿Confirmás eliminar el hospital <strong>${esc(hospital.identificacion)}</strong>?`,
    );
    if (!ok) return;
    try {
      await this.service.delete(hospital._id!);
      this.helper.notifSuccess('Hospital eliminado');
      this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla

  private setColumnas(): void {
    const columnas: IColumnas<IHospital>[] = [
      {
        header: { label: 'Hospital', sort: 'identificacion', sortable: true },
        row: {
          html: (d) =>
            htmlEntidad('local_hospital', d.identificacion, [
              d.valores?.gestion
                ? `Gestión ${esc(d.valores.gestion.toLowerCase())}`
                : null,
            ]),
        },
      },
      {
        header: { label: 'Estado', sort: 'valores.activo', sortable: true },
        row: { estado: (d) => estadoActivo(d.valores?.activo) },
      },
      {
        header: { label: 'Dirección', sort: 'direccion', sortable: true },
        row: {
          html: (d) => htmlDireccion(d.direccion),
          tooltip: (d) => tooltipDireccion(d.direccion),
        },
      },
      {
        header: { label: 'Contacto' },
        row: {
          html: (d) => htmlContacto(d.valores?.telefono, d.valores?.email),
        },
      },
      {
        header: {
          label: 'Fecha de alta',
          sort: 'fechaCreacion',
          sortable: true,
          hidden: true,
        },
        row: { fecha: (d) => d.fechaCreacion, noData: 'Sin fecha' },
      },
    ];

    if (HelperService.getTipoCliente() !== 'Final') {
      columnas.push({
        header: { label: 'Cliente', hidden: true },
        row: {
          html: (d) => this.clienteJerarquia.htmlNombre(d.cliente),
          tooltip: (d) => this.clienteJerarquia.tooltipJerarquia(d.cliente),
          noData: 'Sin cliente',
        },
      });
    }

    columnas.push({
      header: { label: 'Acciones' },
      row: {
        // Menú: Ver detalle primero, Eliminar al final
        accionesGrouped: [
          {
            tipo: 'material',
            icon: 'visibility',
            tooltip: 'Ver detalle',
            click: (d) => this.detalles(d),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'edit',
            tooltip: 'Editar',
            click: (d) => this.editar(d),
            disabled: () =>
              !this.helper.tienePermiso(
                'Emergencias Médicas - Editar hospitales',
              ),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (d) => this.eliminar(d),
            disabled: () =>
              !this.helper.tienePermiso(
                'Emergencias Médicas - Eliminar hospitales',
              ),
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  // Hooks

  ngOnInit(): void {
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.unshift(this.cliente, this.ancestro);
    }
    this.setColumnas();
    this.listar();
  }

  ngOnDestroy(): void {
    this.datos$?.unsubscribe();
  }
}
